import React from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { useToggle } from '@/hooks/useToggle'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { CountdownToMidnight } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GiveawayBanners/CountdownToMidnight'

interface GiveawayBannerProps {
  theatricalSlug: string
  className?: string
  height?: string
}

export const GiveawayBanner: React.FC<GiveawayBannerProps> = ({ theatricalSlug, className, height = '30%' }) => {
  const { t } = useTranslate('tickets')
  const [isYoutubeModalOpen, toggleIsYoutubeModalOpen] = useToggle(false)
  const { isActive: isGiveawayActive, dailyBonusEntries, refetch } = useGiveawaySchedule(theatricalSlug)

  const supportedSlugs = [slugs.kingOfKings, slugs.theLastRodeo]

  if (!supportedSlugs.includes(theatricalSlug) || !isGiveawayActive) return null

  const bannerImagesBySlug = {
    [slugs.kingOfKings]: [
      {
        viewport: 'desktop',
        className: 'relative hidden lg:block',
        src: '/v1741723920/king-of-kings/tkok-giveaway-banner-2x-desk-01.webp',
        width: 2000,
        height: 334,
      },
      {
        viewport: 'tablet',
        className: 'relative hidden md:block lg:hidden',
        src: '/v1741723919/king-of-kings/tkok-giveaway-banner-2x-tablet-01.webp',
        width: 1024,
        height: 200,
      },
      {
        viewport: 'mobile',
        className: 'relative md:hidden',
        src: '/v1741723919/king-of-kings/tkok-giveaway-banner-2x-mobile-01.webp',
        width: 640,
        height: 180,
      },
    ],
    [slugs.theLastRodeo]: [
      {
        viewport: 'desktop',
        className: 'relative hidden lg:block',
        src: '/v1743119108/the-last-rodeo/last-rodeo-giveaway-banner-2x-desk.webp',
        width: 2000,
        height: 334,
      },
      {
        viewport: 'tablet',
        className: 'relative hidden md:block lg:hidden',
        src: '/v1743119108/the-last-rodeo/last-rodeo-giveaway-banner-2x-tablet.webp',
        width: 1024,
        height: 200,
      },
      {
        viewport: 'mobile',
        className: 'relative md:hidden',
        src: '/v1743119108/the-last-rodeo/last-rodeo-giveaway-banner-2x-mobile.webp',
        width: 640,
        height: 180,
      },
    ],
  }

  const bannerImages = bannerImagesBySlug[theatricalSlug]

  return (
    <div className={className}>
      <div
        onClick={() => toggleIsYoutubeModalOpen()}
        className="relative w-full cursor-pointer overflow-hidden rounded-xl !pr-0 @container"
      >
        {bannerImages?.map((image) => (
          <div key={image.viewport} className={image.className}>
            <Image
              className="h-auto w-full"
              src={image.src}
              alt={`${theatricalSlug === slugs.kingOfKings ? 'King of Kings' : 'Last Rodeo'} giveaway banner`}
              width={image.width}
              height={image.height}
            />
            <div
              className={classNames(
                `absolute !mb-0 bottom-0 left-0 flex w-[95%] md:w-[85%] items-center @container/text`,
                className,
              )}
              style={{ height }}
            >
              <div className="absolute left-[3.8%]">
                <div
                  className={classNames(
                    'flex items-center justify-center gap-3 font-whitney text-[0.65em] font-normal  @xs/text:text-[12px] @sm/text:text-xs @md/text:text-xs @2xl/text:text-sm @3xl/text:text-base @5xl/text:text-xl md:justify-start',
                    theatricalSlug === slugs.theLastRodeo ? '!text-black !font-semibold' : '!text-white',
                  )}
                >
                  <div className="hidden md:block">
                    <Translate i18nKey="tkokbonusentriesbannerv3" t={t} values={{ dailyBonusEntries }}>
                      Get {{ dailyBonusEntries }} Bonus Entries Today!
                    </Translate>
                  </div>
                  <div className="md:hidden">
                    <Translate i18nKey="tkokbonusentriesbannerv3mobile" t={t} values={{ dailyBonusEntries }}>
                      {{ dailyBonusEntries }} Bonus Entries Today!
                    </Translate>
                  </div>
                  <div
                    className={
                      'flex items-center justify-center @xs/text:text-[12px] @sm/text:text-xs @sm:text-sm @md/text:text-xs @lg/text:text-sm @2xl/text:text-sm @3xl/text:text-base @5xl/text:text-xl'
                    }
                  >
                    <div>{t('bannerEndsIn', 'Ends In:')}</div>
                    <CountdownToMidnight
                      onCountdownEnd={refetch}
                      textColorClass={theatricalSlug === slugs.theLastRodeo ? '!text-black' : '!text-white'}
                      numberClassName={
                        '@xs/text:text-[12px]  @sm/text:text-xs @md/text:text-xs @lg/text:text-xs @2xl/text:text-sm @xs/text:!py-0.5 @xs/text:!px-1 @2xl/text:!py-[2px] @2xl/text:!px-[5px] @lg/text:leading-[14px] @2xl/text:leading-[18px] @3xl/text:leading-[18px] @3xl/text:text-base @5xl/text:text-xl'
                      }
                    />{' '}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {theatricalSlug === slugs.kingOfKings && (
        <GBFadeInExperiment experimentName="king-of-kings-video" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <YoutubeVideoModal
              youTubeUrl={'https://www.youtube-nocookie.com/embed/tOudE4H1kvM'}
              showModal={isYoutubeModalOpen}
              handleClose={() => toggleIsYoutubeModalOpen(false)}
              className="!z-[60]"
            />
          </ExperimentVariation>
          <ExperimentVariation default>
            <YoutubeVideoModal
              youTubeUrl={'https://www.youtube-nocookie.com/embed/85jmrZfkghE'}
              showModal={isYoutubeModalOpen}
              handleClose={() => toggleIsYoutubeModalOpen(false)}
              className="!z-[60]"
            />
          </ExperimentVariation>
        </GBFadeInExperiment>
      )}
      {theatricalSlug === slugs.theLastRodeo && (
        <YoutubeVideoModal
          youTubeUrl={'https://www.youtube-nocookie.com/embed/3NT-hU2jIvk'}
          showModal={isYoutubeModalOpen}
          handleClose={() => toggleIsYoutubeModalOpen(false)}
          className="!z-[60]"
        />
      )}
    </div>
  )
}
